import React, { useState } from "react";
import Layout from "@/components/Layout";
import GradientStrip from "@/components/GradientStrip";
import SEO from "@/components/SEO";
import { api } from "@/lib/config";
import { CheckCircle2, BookOpen, Download, ArrowRight } from "lucide-react";

const PRINCIPLES = [
  "Clarifier le cadre",
  "Tenir la posture",
  "Désamorcer les tensions",
  "Décider avec discernement",
  "Restaurer la confiance",
  "Doser exigence et bienveillance",
];

export default function ArtDeLaPaix() {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    consent: true,
  });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const setField = (k) => (e) =>
    setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    setErrorMsg("");
    try {
      const { data } = await api.post("/livrets/art-de-la-paix", form);
      if (data?.ok) setStatus("success");
      else throw new Error("Réponse invalide");
    } catch (err) {
      setStatus("error");
      setErrorMsg(
        err?.response?.data?.detail ||
          "Une erreur est survenue. Merci de réessayer dans quelques instants."
      );
    }
  };

  return (
    <Layout headerVariant="dark">
      <SEO
        title="L'art de la paix du Manager · Livret offert"
        description="Téléchargez gratuitement le livret « L'art de la paix du Manager » : 13 principes concrets pour apaiser les tensions, clarifier le cadre et renforcer la confiance dans votre équipe."
        path="/artdelapaix"
      />

      {/* HERO */}
      <section data-testid="livret-hero" className="bg-brand-deep text-white pt-40 md:pt-48 pb-20">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
          <p className="section-num text-white/85 fade-up">Livret offert · 13 principes</p>
          <h1 className="display text-5xl md:text-7xl lg:text-[88px] leading-[0.95] mt-8 max-w-5xl fade-up-2">
            L'art de la <em className="not-italic text-accent-pink">paix</em> du Manager.
          </h1>
          <p className="text-white/90 text-lg md:text-xl leading-relaxed mt-8 max-w-3xl fade-up-3">
            13 principes concrets pour apaiser les tensions, clarifier le cadre et renforcer la
            confiance au sein de votre équipe. Le fruit d'années d'expérience en direction des
            opérations et de coaching de dirigeants.
          </p>
        </div>
        <GradientStrip />
      </section>

      {/* CONTENT + FORM */}
      <section className="bg-paper py-20 md:py-28">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* LEFT — Pitch & contenu */}
          <div className="lg:col-span-7" data-testid="livret-content">
            <p className="section-num">À l'intérieur</p>
            <h2 className="display text-3xl md:text-4xl leading-tight mt-6 text-ink">
              Pour les managers qui veulent tenir leur rôle <em className="not-italic text-accent-pink">sans s'épuiser</em>.
            </h2>

            <div className="prose-wedo text-ink/80 text-[16px] leading-relaxed mt-8 space-y-5 max-w-2xl">
              <p>
                Diriger une équipe au quotidien, c'est faire face à des tensions, des conflits
                latents, des arbitrages permanents — et tenir le cap, sans perdre en exigence ni en
                humanité.
              </p>
              <p>
                Ce livret rassemble <strong>13 principes pratiques</strong>, issus de mon
                expérience de coach professionnel et d'ancien directeur des opérations, pour vous
                aider à ajuster votre posture managériale, désamorcer les conflits structurants et
                installer une culture d'équipe plus apaisée.
              </p>
              <p>
                Pas de théorie, pas de catalogue. Des repères concrets, applicables dès demain.
              </p>
            </div>

            <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
              {PRINCIPLES.map((p) => (
                <li
                  key={p}
                  className="flex items-start gap-3 text-[15px] text-ink"
                  data-testid="livret-principle"
                >
                  <CheckCircle2 size={20} className="text-accent-pink shrink-0 mt-0.5" />
                  <span>{p}</span>
                </li>
              ))}
              <li className="flex items-start gap-3 text-[15px] text-ink/60 italic">
                <span className="text-accent-pink shrink-0 mt-0.5">+</span>
                <span>… et 7 autres principes essentiels.</span>
              </li>
            </ul>

            <div className="mt-12 flex items-center gap-4 text-ink/70">
              <BookOpen size={18} className="text-brand" />
              <span className="text-[14px] uppercase tracking-wider">
                Livret PDF · 13 principes · Lecture 20 min
              </span>
            </div>
          </div>

          {/* RIGHT — Formulaire */}
          <div className="lg:col-span-5">
            <div
              className="bg-brand-deep text-white p-8 md:p-10 lg:p-12 lg:sticky lg:top-28"
              data-testid="livret-form-card"
            >
              {status === "success" ? (
                <div className="text-center py-8" data-testid="livret-success">
                  <div className="mx-auto w-16 h-16 rounded-full bg-accent-pink/20 flex items-center justify-center mb-6">
                    <CheckCircle2 size={32} className="text-accent-pink" />
                  </div>
                  <h3 className="display text-2xl md:text-3xl leading-tight">
                    Le livret est en route.
                  </h3>
                  <p className="text-white/85 leading-relaxed mt-6">
                    Vérifiez votre boîte mail <strong>{form.email}</strong> dans quelques instants.
                    Si vous ne le voyez pas, pensez à regarder dans vos spams ou indésirables.
                  </p>
                  <a
                    href="/livrets/art-de-la-paix-du-manager.pdf"
                    download
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 mt-8 bg-accent-pink text-white px-6 py-3 rounded-full text-sm font-semibold hover:opacity-90 transition"
                    data-testid="livret-download-direct"
                  >
                    <Download size={16} /> Télécharger directement
                  </a>
                </div>
              ) : (
                <>
                  <p className="text-[11px] font-bold tracking-[0.3em] uppercase text-accent-pink mb-4">
                    Recevoir le livret
                  </p>
                  <h3 className="display text-2xl md:text-3xl leading-tight">
                    Téléchargez le livret <em className="not-italic text-accent-pink">offert</em>.
                  </h3>
                  <p className="text-white/80 text-[14px] mt-4 leading-relaxed">
                    Indiquez vos coordonnées, nous vous l'envoyons immédiatement par email.
                  </p>

                  <form onSubmit={submit} className="mt-8 space-y-4" data-testid="livret-form">
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="text"
                        required
                        placeholder="Prénom"
                        value={form.first_name}
                        onChange={setField("first_name")}
                        className="bg-white/10 border border-white/20 px-4 py-3 text-[14px] text-white placeholder:text-white/50 focus:outline-none focus:border-accent-pink transition"
                        data-testid="livret-input-firstname"
                      />
                      <input
                        type="text"
                        required
                        placeholder="Nom"
                        value={form.last_name}
                        onChange={setField("last_name")}
                        className="bg-white/10 border border-white/20 px-4 py-3 text-[14px] text-white placeholder:text-white/50 focus:outline-none focus:border-accent-pink transition"
                        data-testid="livret-input-lastname"
                      />
                    </div>
                    <input
                      type="email"
                      required
                      placeholder="Email professionnel"
                      value={form.email}
                      onChange={setField("email")}
                      className="w-full bg-white/10 border border-white/20 px-4 py-3 text-[14px] text-white placeholder:text-white/50 focus:outline-none focus:border-accent-pink transition"
                      data-testid="livret-input-email"
                    />
                    <input
                      type="tel"
                      placeholder="Téléphone (optionnel)"
                      value={form.phone}
                      onChange={setField("phone")}
                      className="w-full bg-white/10 border border-white/20 px-4 py-3 text-[14px] text-white placeholder:text-white/50 focus:outline-none focus:border-accent-pink transition"
                      data-testid="livret-input-phone"
                    />

                    <label className="flex items-start gap-3 text-[12px] text-white/75 pt-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.consent}
                        onChange={setField("consent")}
                        className="mt-1 accent-accent-pink"
                        data-testid="livret-consent"
                      />
                      <span>
                        J'accepte de recevoir le livret et, occasionnellement, des ressources Wedo
                        sur le management et le leadership. Je peux me désinscrire à tout moment.
                      </span>
                    </label>

                    {status === "error" && (
                      <p
                        className="text-accent-pink text-[13px] bg-accent-pink/10 px-4 py-2 mt-2"
                        data-testid="livret-error"
                      >
                        {errorMsg}
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={status === "sending"}
                      className="w-full bg-accent-pink text-white px-6 py-4 text-[14px] font-bold tracking-wider uppercase hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      data-testid="livret-submit"
                    >
                      {status === "sending" ? "Envoi en cours…" : (
                        <>
                          Recevoir le livret
                          <ArrowRight size={16} />
                        </>
                      )}
                    </button>

                    <p className="text-[11px] text-white/50 pt-2">
                      Vos données ne sont partagées avec personne. Conformément au RGPD, vous
                      pouvez demander leur suppression à tout moment.
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
